import { useMemo } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import type {
  LegacyReportDraftData,
  LegacyReportReviewState,
} from "../../../types/legacyReportReview";

interface Props {
  latest: LegacyReportReviewState;
  current: LegacyReportDraftData;
  mayRetainEdits: boolean;
  onClose: () => void;
  onLoad: () => void;
  onKeep: () => void;
}

export default function LegacyReportDraftConflict({
  latest,
  current,
  mayRetainEdits,
  onClose,
  onLoad,
  onKeep,
}: Props) {
  const sections = useMemo(
    () =>
      current.sections.map((section) => ({
        current: section,
        saved: latest.draft?.data.sections.find(
          (candidate) => candidate.id === section.id
        ),
      })),
    [current.sections, latest.draft]
  );
  return (
    <Modal
      opened
      onClose={onClose}
      title={`Saved reconstruction · revision ${
        latest.draft?.revision ?? "unknown"
      }`}
      size="xl"
      centered
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Alert color="orange">
          Your local edits are retained. Review the saved version before
          choosing which content to keep.{" "}
          {latest.baseline
            ? "The saved reconstruction is approved and immutable."
            : !mayRetainEdits
            ? "The source or section structure changed; load the saved reconstruction to continue."
            : ""}
        </Alert>
        <ScrollArea.Autosize mah="55vh">
          <Table.ScrollContainer minWidth={500}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Section</Table.Th>
                  <Table.Th>Your edits</Table.Th>
                  <Table.Th>Saved content</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sections.map(({ current: local, saved }) => (
                  <Table.Tr key={local.id}>
                    <Table.Td>{local.label || "Historical text"}</Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                        {local.text}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                        {saved?.text || "No matching saved section"}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </ScrollArea.Autosize>
        <Group justify="space-between">
          <Button variant="default" onClick={onClose}>
            Keep comparing
          </Button>
          <Group>
            <Button variant="default" onClick={onLoad}>
              Load saved reconstruction
            </Button>
            {mayRetainEdits && (
              <Button onClick={onKeep}>Keep my edits for next save</Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
